import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Save, Plus, Trash2, CheckCircle, Wrench, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const MaintenanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [maintenance, setMaintenance] = useState(null);
  const [machinery, setMachinery] = useState(null);
  const [spareParts, setSpareParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    machinery_id: '',
    type: '',
    description: '',
    scheduled_date: '',
    cost: 0,
    status: 'pending',
    workshop_work: '',
    oil_changed: false,
    oil_type: '',
    oil_quantity: 0,
    hydraulic_oil_changed: false,
    hydraulic_oil_type: '',
    hydraulic_oil_quantity: 0,
    filters_changed: '',
    work_performed: '',
    technician_notes: '',
    main_technician: '',
    assistant_technician: '',
    delivery_datetime: '',
    reception_datetime: '',
    downtime_hours: 0,
    priority: 'normal'
  });

  const [sparePartForm, setSparePartForm] = useState({
    part_id: '',
    quantity: 1,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [maintenanceRes, sparePartsRes] = await Promise.all([
        axios.get(`${API}/maintenance/${id}`),
        axios.get(`${API}/spare-parts`)
      ]);
      
      const maintenanceData = maintenanceRes.data;
      setMaintenance(maintenanceData);
      setSpareParts(sparePartsRes.data);
      
      setFormData({
        machinery_id: maintenanceData.machinery_id || '',
        type: maintenanceData.type || '',
        description: maintenanceData.description || '',
        scheduled_date: maintenanceData.scheduled_date || '',
        cost: maintenanceData.cost || 0,
        status: maintenanceData.status || 'pending',
        workshop_work: maintenanceData.workshop_work || '',
        oil_changed: maintenanceData.oil_changed || false,
        oil_type: maintenanceData.oil_type || '',
        oil_quantity: maintenanceData.oil_quantity || 0,
        hydraulic_oil_changed: maintenanceData.hydraulic_oil_changed || false,
        hydraulic_oil_type: maintenanceData.hydraulic_oil_type || '',
        hydraulic_oil_quantity: maintenanceData.hydraulic_oil_quantity || 0,
        filters_changed: maintenanceData.filters_changed || '',
        work_performed: maintenanceData.work_performed || '',
        technician_notes: maintenanceData.technician_notes || '',
        main_technician: maintenanceData.main_technician || '',
        assistant_technician: maintenanceData.assistant_technician || '',
        delivery_datetime: maintenanceData.delivery_datetime || '',
        reception_datetime: maintenanceData.reception_datetime || '',
        downtime_hours: maintenanceData.downtime_hours || 0,
        priority: maintenanceData.priority || 'normal'
      });

      const machineryRes = await axios.get(`${API}/machinery/${maintenanceData.machinery_id}`);
      setMachinery(machineryRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      // Calcular horas de inactividad automáticamente si hay fechas
      let updatedFormData = { ...formData };
      if (formData.reception_datetime && formData.delivery_datetime) {
        const reception = new Date(formData.reception_datetime);
        const delivery = new Date(formData.delivery_datetime);
        const diffMs = delivery - reception;
        const diffHours = diffMs / (1000 * 60 * 60);
        updatedFormData.downtime_hours = parseFloat(diffHours.toFixed(1));
      }
      
      await axios.put(`${API}/maintenance/${id}`, updatedFormData);
      toast.success('Mantenimiento actualizado');
      setEditing(false);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
      console.error(error);
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('¿Marcar este mantenimiento como completado?')) return;
    try {
      await axios.patch(`${API}/maintenance/${id}/complete`);
      toast.success('Mantenimiento marcado como completado');
      fetchData();
    } catch (error) {
      toast.error('Error al completar mantenimiento');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar este mantenimiento? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/maintenance/${id}`);
      toast.success('Mantenimiento eliminado');
      navigate('/maintenance');
    } catch (error) {
      toast.error('Error al eliminar mantenimiento');
    }
  };

  const handleAddSparePart = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('part_id', sparePartForm.part_id);
      formDataToSend.append('quantity', sparePartForm.quantity);
      formDataToSend.append('notes', sparePartForm.notes);

      await axios.post(`${API}/maintenance/${id}/spare-parts`, formDataToSend);
      toast.success('Repuesto agregado');
      setDialogOpen(false);
      setSparePartForm({ part_id: '', quantity: 1, notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar repuesto');
    }
  };

  const handleRemoveSparePart = async (index) => {
    if (!window.confirm('¿Eliminar este repuesto?')) return;
    try {
      await axios.delete(`${API}/maintenance/${id}/spare-parts/${index}`);
      toast.success('Repuesto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar repuesto');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Cargando...</div>;
  }

  if (!maintenance) {
    return <div className="text-center py-12">Mantenimiento no encontrado</div>;
  }

  const totalCostSpareParts = (maintenance.spare_parts_used || []).reduce((sum, part) => sum + (part.total_cost || 0), 0);
  const totalCost = (maintenance.cost || 0) + totalCostSpareParts;

  return (
    <div data-testid="maintenance-detail-page">
      <button
        onClick={() => navigate('/maintenance')}
        className="flex items-center text-slate-600 hover:text-slate-800 mb-6"
        data-testid="back-button"
      >
        <ArrowLeft size={20} className="mr-2" />
        Volver a Mantenimientos
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header Card */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Mantenimiento {maintenance.type}
                </h1>
                <p className="text-slate-600">Maquinaria: {machinery?.name}</p>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`status-badge status-${maintenance.status}`}>
                  {maintenance.status === 'pending' ? 'Pendiente' : 'Completado'}
                </span>
                {!editing && (
                  <button
                    onClick={() => setEditing(true)}
                    className="p-2 hover:bg-slate-100 rounded"
                    data-testid="edit-button"
                  >
                    <Edit size={20} className="text-orange-500" />
                  </button>
                )}
                {editing && (
                  <>
                    <button
                      onClick={handleUpdate}
                      className="p-2 hover:bg-green-50 rounded"
                      data-testid="save-button"
                    >
                      <Save size={20} className="text-green-500" />
                    </button>
                    <button
                      onClick={() => { setEditing(false); fetchData(); }}
                      className="p-2 hover:bg-slate-100 rounded"
                    >
                      <X size={20} className="text-slate-500" />
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-600">Fecha Programada</p>
                <p className="font-medium">{new Date(maintenance.scheduled_date).toLocaleDateString()}</p>
              </div>
              {maintenance.completed_date && (
                <div>
                  <p className="text-sm text-slate-600">Fecha Completado</p>
                  <p className="font-medium">{new Date(maintenance.completed_date).toLocaleDateString()}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-600">Tipo</p>
                <p className="font-medium">{maintenance.type}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Prioridad</p>
                {editing ? (
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="input-field"
                  >
                    <option value="low">Baja</option>
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                ) : (
                  <p className="font-medium capitalize">{maintenance.priority || 'Normal'}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-slate-600">Costo Mano de Obra</p>
                {editing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                    className="input-field mono"
                  />
                ) : (
                  <p className="text-xl font-bold mono text-orange-600">Bs. {(maintenance.cost || 0).toFixed(2)}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-slate-600">Costo Total</p>
                <p className="text-2xl font-bold mono text-orange-600">Bs. {totalCost.toFixed(2)}</p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-2">Descripción</p>
              {editing ? (
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field"
                  rows={2}
                />
              ) : (
                <p className="text-slate-800">{maintenance.description}</p>
              )}
            </div>
          </div>

          {/* Personnel Info */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Personal y Fechas
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Técnico Principal
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.main_technician}
                    onChange={(e) => setFormData({ ...formData, main_technician: e.target.value })}
                    className="input-field"
                    placeholder="Nombre del técnico principal"
                  />
                ) : (
                  <p className="text-slate-800 bg-slate-50 p-3 rounded">{formData.main_technician || 'No especificado'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Técnico Ayudante
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.assistant_technician}
                    onChange={(e) => setFormData({ ...formData, assistant_technician: e.target.value })}
                    className="input-field"
                    placeholder="Nombre del ayudante"
                  />
                ) : (
                  <p className="text-slate-800 bg-slate-50 p-3 rounded">{formData.assistant_technician || 'No especificado'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Fecha/Hora Recepción
                </label>
                {editing ? (
                  <input
                    type="datetime-local"
                    value={formData.reception_datetime}
                    onChange={(e) => setFormData({ ...formData, reception_datetime: e.target.value })}
                    className="input-field"
                  />
                ) : (
                  <p className="text-slate-800 bg-slate-50 p-3 rounded">
                    {formData.reception_datetime ? new Date(formData.reception_datetime).toLocaleString() : 'No especificado'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Fecha/Hora Entrega
                </label>
                {editing ? (
                  <input
                    type="datetime-local"
                    value={formData.delivery_datetime}
                    onChange={(e) => setFormData({ ...formData, delivery_datetime: e.target.value })}
                    className="input-field"
                  />
                ) : (
                  <p className="text-slate-800 bg-slate-50 p-3 rounded">
                    {formData.delivery_datetime ? new Date(formData.delivery_datetime).toLocaleString() : 'No especificado'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Horas de Inactividad
                </label>
                {editing ? (
                  <input
                    type="number"
                    step="0.1"
                    value={formData.downtime_hours}
                    onChange={(e) => setFormData({ ...formData, downtime_hours: parseFloat(e.target.value) })}
                    className="input-field mono"
                    placeholder="Horas fuera de servicio"
                  />
                ) : (
                  <p className="text-slate-800 bg-slate-50 p-3 rounded mono">{formData.downtime_hours || 0} hrs</p>
                )}
              </div>
            </div>
          </div>

          {/* Work Details */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h3 className="text-xl font-semibold mb-4 flex items-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Wrench size={20} className="mr-2 text-orange-500" />
              Detalles del Trabajo
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Trabajos Realizados en Taller
                </label>
                {editing ? (
                  <textarea
                    value={formData.workshop_work}
                    onChange={(e) => setFormData({ ...formData, workshop_work: e.target.value })}
                    className="input-field"
                    rows={3}
                    placeholder="Ej: Revisión general, ajustes, soldaduras, reparaciones..."
                  />
                ) : (
                  <p className="text-slate-800 bg-slate-50 p-3 rounded whitespace-pre-wrap">
                    {formData.workshop_work || 'No especificado'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Trabajos Realizados (Detalle)
                </label>
                {editing ? (
                  <textarea
                    value={formData.work_performed}
                    onChange={(e) => setFormData({ ...formData, work_performed: e.target.value })}
                    className="input-field"
                    rows={4}
                    placeholder="Detalle de todos los trabajos realizados..."
                  />
                ) : (
                  <p className="text-slate-800 bg-slate-50 p-3 rounded whitespace-pre-wrap">
                    {formData.work_performed || 'No especificado'}
                  </p>
                )}
              </div>

              {/* Oil Changes */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-semibold text-slate-800 mb-3">Cambios de Aceite</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Engine Oil */}
                  <div className="bg-slate-50 p-4 rounded border border-slate-200">
                    <div className="flex items-center mb-3">
                      {editing ? (
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.oil_changed}
                            onChange={(e) => setFormData({ ...formData, oil_changed: e.target.checked })}
                            className="mr-2 w-4 h-4"
                          />
                          <span className="font-medium">Aceite Motor</span>
                        </label>
                      ) : (
                        <div className="flex items-center">
                          <span className={`mr-2 text-lg ${formData.oil_changed ? 'text-green-500' : 'text-red-500'}`}>
                            {formData.oil_changed ? '✓' : '✗'}
                          </span>
                          <span className="font-medium">Aceite Motor</span>
                        </div>
                      )}
                    </div>
                    {(editing || formData.oil_changed) && (
                      <>
                        <div className="mb-2">
                          <label className="block text-xs text-slate-600 mb-1">Tipo</label>
                          {editing ? (
                            <input
                              type="text"
                              value={formData.oil_type}
                              onChange={(e) => setFormData({ ...formData, oil_type: e.target.value })}
                              className="input-field"
                              placeholder="15W-40, 10W-30..."
                            />
                          ) : (
                            <p className="text-sm">{formData.oil_type || 'N/A'}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-slate-600 mb-1">Cantidad (L)</label>
                          {editing ? (
                            <input
                              type="number"
                              step="0.1"
                              value={formData.oil_quantity}
                              onChange={(e) => setFormData({ ...formData, oil_quantity: parseFloat(e.target.value) || 0 })}
                              className="input-field"
                            />
                          ) : (
                            <p className="text-sm mono">{formData.oil_quantity || 0} L</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Hydraulic Oil */}
                  <div className="bg-slate-50 p-4 rounded border border-slate-200">
                    <div className="flex items-center mb-3">
                      {editing ? (
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.hydraulic_oil_changed}
                            onChange={(e) => setFormData({ ...formData, hydraulic_oil_changed: e.target.checked })}
                            className="mr-2 w-4 h-4"
                          />
                          <span className="font-medium">Aceite Hidráulico</span>
                        </label>
                      ) : (
                        <div className="flex items-center">
                          <span className={`mr-2 text-lg ${formData.hydraulic_oil_changed ? 'text-green-500' : 'text-red-500'}`}>
                            {formData.hydraulic_oil_changed ? '✓' : '✗'}
                          </span>
                          <span className="font-medium">Aceite Hidráulico</span>
                        </div>
                      )}
                    </div>
                    {(editing || formData.hydraulic_oil_changed) && (
                      <>
                        <div className="mb-2">
                          <label className="block text-xs text-slate-600 mb-1">Tipo</label>
                          {editing ? (
                            <input
                              type="text"
                              value={formData.hydraulic_oil_type}
                              onChange={(e) => setFormData({ ...formData, hydraulic_oil_type: e.target.value })}
                              className="input-field"
                              placeholder="AW-68, ISO 46..."
                            />
                          ) : (
                            <p className="text-sm">{formData.hydraulic_oil_type || 'N/A'}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-slate-600 mb-1">Cantidad (L)</label>
                          {editing ? (
                            <input
                              type="number"
                              step="0.1"
                              value={formData.hydraulic_oil_quantity}
                              onChange={(e) => setFormData({ ...formData, hydraulic_oil_quantity: parseFloat(e.target.value) || 0 })}
                              className="input-field"
                            />
                          ) : (
                            <p className="text-sm mono">{formData.hydraulic_oil_quantity || 0} L</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Filtros Cambiados
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.filters_changed}
                    onChange={(e) => setFormData({ ...formData, filters_changed: e.target.value })}
                    className="input-field"
                    placeholder="Filtro de aceite, filtro de aire, filtro de combustible..."
                  />
                ) : (
                  <p className="text-slate-800 bg-slate-50 p-3 rounded">{formData.filters_changed || 'Ninguno'}</p>
                )}
              </div>

              {/* Technician Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Notas del Técnico
                </label>
                {editing ? (
                  <textarea
                    value={formData.technician_notes}
                    onChange={(e) => setFormData({ ...formData, technician_notes: e.target.value })}
                    className="input-field"
                    rows={3}
                    placeholder="Observaciones adicionales, recomendaciones, próximos mantenimientos..."
                  />
                ) : (
                  <p className="text-slate-800 bg-slate-50 p-3 rounded whitespace-pre-wrap">
                    {formData.technician_notes || 'Sin notas'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Spare Parts Used */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Repuestos Utilizados
              </h3>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-spare-part-button">
                    <Plus size={18} className="mr-2" />
                    Agregar Repuesto
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Agregar Repuesto</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddSparePart} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Repuesto</label>
                      <select
                        value={sparePartForm.part_id}
                        onChange={(e) => setSparePartForm({ ...sparePartForm, part_id: e.target.value })}
                        className="input-field"
                        required
                      >
                        <option value="">Seleccionar...</option>
                        {spareParts.map((part) => (
                          <option key={part.id} value={part.id}>
                            {part.name} - {part.part_number} (Stock: {part.quantity})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Cantidad</label>
                      <input
                        type="number"
                        min="1"
                        value={sparePartForm.quantity}
                        onChange={(e) => setSparePartForm({ ...sparePartForm, quantity: parseInt(e.target.value) })}
                        className="input-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Notas (opcional)</label>
                      <textarea
                        value={sparePartForm.notes}
                        onChange={(e) => setSparePartForm({ ...sparePartForm, notes: e.target.value })}
                        className="input-field"
                        rows={2}
                      />
                    </div>
                    <div className="flex justify-end space-x-3">
                      <Button type="button" onClick={() => setDialogOpen(false)} className="btn-secondary">
                        Cancelar
                      </Button>
                      <Button type="submit" className="btn-primary">
                        Agregar
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {maintenance.spare_parts_used && maintenance.spare_parts_used.length > 0 ? (
              <div className="space-y-3">
                {maintenance.spare_parts_used.map((part, index) => (
                  <div key={`${part.part_id}-${part.added_at}`} className="flex items-center justify-between border border-slate-200 rounded p-4">
                    <div className="flex-1">
                      <p className="font-medium">{part.part_name}</p>
                      <p className="text-sm text-slate-600 mono">Parte N°: {part.part_number}</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm">
                        <span>Cantidad: <span className="font-medium">{part.quantity}</span></span>
                        <span>Costo Unit: <span className="mono">Bs. {part.unit_cost.toFixed(2)}</span></span>
                        <span className="font-bold text-orange-600 mono">Total: Bs. {part.total_cost.toFixed(2)}</span>
                      </div>
                      {part.notes && <p className="text-sm text-slate-600 mt-2 italic">{part.notes}</p>}
                    </div>
                    <button
                      onClick={() => handleRemoveSparePart(index)}
                      className="ml-4 p-2 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={18} className="text-red-500" />
                    </button>
                  </div>
                ))}
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total en Repuestos:</span>
                    <span className="text-2xl font-bold mono text-orange-600">Bs. {totalCostSpareParts.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center py-8 text-slate-500">No se han agregado repuestos</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div className="bg-white border border-slate-200 rounded-md p-6 sticky top-6 space-y-4">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Acciones
            </h3>
            
            {maintenance.status === 'pending' && (
              <button
                onClick={handleComplete}
                className="w-full btn-primary flex items-center justify-center"
                data-testid="complete-maintenance-button"
              >
                <CheckCircle size={18} className="mr-2" />
                Marcar como Completado
              </button>
            )}

            <button
              onClick={handleDelete}
              className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-3 px-4 rounded flex items-center justify-center transition-colors"
              data-testid="delete-maintenance-button"
            >
              <Trash2 size={18} className="mr-2" />
              Eliminar Mantenimiento
            </button>

            <div className="border-t border-slate-200 pt-4 mt-4">
              <h4 className="font-semibold mb-3">Resumen de Costos</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Mano de Obra:</span>
                  <span className="mono">Bs. {(maintenance.cost || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Repuestos:</span>
                  <span className="mono">Bs. {totalCostSpareParts.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-200 font-bold text-base">
                  <span>Total:</span>
                  <span className="mono text-orange-600">Bs. {totalCost.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {formData.downtime_hours > 0 && (
              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-semibold mb-2">Tiempo de Inactividad</h4>
                <p className="text-2xl font-bold mono text-red-600">{formData.downtime_hours} hrs</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDetail;
